import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2, _AbDropdownMachine;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbListMachine } from "abreact/incubator/ui/component/AbListMachine";
import { BaseMachine } from "abreact/incubator/ui/machine/BaseMachine";
import { PopupPosition } from "abreact/incubator/ui/popup/PopupPosition";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { CancelablePromise, PromiseCanceled } from "abweb/utils/PromiseUtils";
import { action, computed, observable, runInAction, makeObservable } from "mobx";

/**
 * The type of a function that can provide items.
 */

/**
 * Properties for a dropdown machine.
 */

/**
 * The default debounce delay to use, in milliseconds.
 * The debounce delay is how long after a user changes text we wait to decide
 * that they're done making inputs, at which point we often make
 * an expensive server call. Usually that's the time between keystrokes.
 *
 * Usages that make inexpensive (e.g. synchronous wrapped in an async function)
 * calls can override the debounceDelay prop with a smaller value.
 *
 * See ABSDK-1922 for more details.
 */
export const DEFAULT_DEBOUNCE_DELAY = 400;

/**
 * A state machine for managing dropdowns.
 */
export let AbDropdownMachine = (_class = (_AbDropdownMachine = class AbDropdownMachine extends BaseMachine {
  constructor(props) {
    super();
    _defineProperty(this, "_values", observable.array([], {
      deep: false
    }));
    _initializerDefineProperty(this, "showingPopup", _descriptor, this);
    _initializerDefineProperty(this, "_popupPosition", _descriptor2, this);
    _defineProperty(this, "_initialPopupPosition", void 0);
    _defineProperty(this, "listMachine", void 0);
    _defineProperty(this, "autoAccept", void 0);
    _defineProperty(this, "_itemsFunction", void 0);
    _defineProperty(this, "_onItemChosenCallback", void 0);
    _defineProperty(this, "_onValuesChosenCallback", void 0);
    _defineProperty(this, "currentItemsPromise", void 0);
    _defineProperty(this, "refreshItemsTimeout", void 0);
    _defineProperty(this, "debounceDelay", DEFAULT_DEBOUNCE_DELAY);
    /**
     * When list selection changes announce that the value of the dropdown changed rather than waiting until list is closed.
     * Set this._value to the new value(s) and make the announcement. If we do not set here and user hits ESCape the change
     * will be lost.
     */
    _defineProperty(this, "onSelectionChanged", () => {
      if (this.autoAccept) {
        // Also need to set values unless we are completely controlled (_values is a class member, it's ours)
        // Cannot use values setter because its a feedback loop
        runInAction(() => this._values.replace(this.listMachine.selectionManager.selectedItems));
        this.onValuesChosen(this.listMachine.selectionManager.selectedItems);
      }
    });
    makeObservable(this);
    this._popupPosition = undefined;
    this.showingPopup = false;
    this.listMachine = new AbListMachine({
      selectionManager: props.selectionManager,
      selectionMode: props.selectionMode,
      itemDisplayStringFunction: props.itemDisplayStringFunction,
      valuesEqual: props.valuesEqual
    });
    this.registerDestroyListener(this.listMachine);
    this.autoAccept = props.autoAccept || false;
    this._itemsFunction = props.itemsFunction;
    this._initialPopupPosition = props.popupPosition || PopupPosition.BELOW;
    this.popupPosition = this._initialPopupPosition;
    if (props.items) {
      this.items = props.items;
    }
    if (props.values) {
      this.values = props.values;
    }
    this._onItemChosenCallback = props.onItemChosen;
    this._onValuesChosenCallback = props.onValuesChosen;
    this.debounceDelay = props.debounceDelay !== undefined ? props.debounceDelay : DEFAULT_DEBOUNCE_DELAY;

    // Setup the ability to autoAccept or not
    this.registerDestroyListener(this.listMachine.selectionManager.observeSelectedItems(() => this.onSelectionChanged));
  }
  get itemsFunction() {
    return this._itemsFunction;
  }
  get itemDisplayStringFunction() {
    return this.listMachine.itemDisplayStringFunction;
  }
  get onItemChosenCallback() {
    return this._onItemChosenCallback;
  }
  onShowPopup() {
    this.listMachine.selectedItems = this.values;
    this.showingPopup = true;
  }
  onHidePopup() {
    this.showingPopup = false;
    this.popupPosition = this._initialPopupPosition;
    this.listMachine.clear();
  }
  get popupPosition() {
    return this._popupPosition;
  }
  set popupPosition(position) {
    this._popupPosition = position;
  }
  get items() {
    return this.listMachine.items;
  }
  set items(items) {
    this.listMachine.items = items;
  }

  /**
   * The values selected by the user.
   */
  get values() {
    return this._values ? this._values.slice() : [];
  }
  set values(values) {
    if (this.hasDifferentValues(values)) {
      runInAction(() => this._values.replace(values));
      this.onValuesChange(values);
    }
  }
  hasDifferentValues(newValues) {
    const count = this.values.length;
    if (newValues.length !== count) {
      return true;
    }
    for (let i = 0; i < count; i++) {
      if (!this.listMachine.valuesEqual(this.values[i], newValues[i])) {
        return true;
      }
    }
    return false;
  }
  onValuesChange(values) {
    this.listMachine.selectedItems = values;
  }
  get selectionMode() {
    return this.listMachine.selectionMode;
  }
  set selectionMode(mode) {
    this.listMachine.selectionMode = mode;
  }

  /**
   * Returns the current values of the popup, or undefined if none has been selected.
   *
   * Note: for multiple selection, the value will be the set of selected items.
   */
  get popupSelectedItems() {
    if (this.listMachine.selectionMode === SelectionMode.SINGLE) {
      const selectedItem = this.listMachine.selectedItem;
      return selectedItem !== undefined ? [selectedItem] : [];
    } else {
      return Array.from(this.listMachine.selectedItems);
    }
  }
  get selectedItem() {
    return this.listMachine.selectedItem;
  }
  itemLabel(item) {
    return this.itemDisplayStringFunction(item);
  }
  onPopupSelectItem(item) {
    if (this.autoAccept) {
      this.onItemChosen(item);
    }
  }
  onItemChosen(item) {
    this._onItemChosenCallback && this._onItemChosenCallback(item);
    if (this.selectionMode === SelectionMode.MULTIPLE) {
      if (!this.hasValue(item)) {
        this.onValuesChosenIfChanged([...this.values, item]);
      }
    } else {
      this.onValuesChosenIfChanged(item !== undefined ? [item] : []);
    }
  }
  get currentValues() {
    return this.values;
  }
  hasValue(item) {
    const itemIndex = this.values.findIndex(value => {
      return this.listMachine.valuesEqual(value, item);
    });
    return itemIndex >= 0;
  }
  onValuesChosenIfChanged(chosenValues) {
    if (this.hasDifferentValues(chosenValues)) {
      this.values = chosenValues;
      this.onValuesChosen(chosenValues);
    }
  }
  onValuesChosen(chosenValues) {
    this._onValuesChosenCallback && this._onValuesChosenCallback(chosenValues);
  }
  onRemoveValue(value) {
    const updatedValues = this.values.filter(item => !this.listMachine.valuesEqual(item, value));
    this.onValuesChosenIfChanged(updatedValues);
  }
  selectNextItem() {
    if (this.listMachine.selectNextItem()) {
      this.onPopupSelectItem(this.listMachine.selectedItem);
    }
  }
  selectPreviousItemOrClear() {
    if (this.listMachine.selectPreviousItem()) {
      this.onPopupSelectItem(this.listMachine.selectedItem);
    } else {
      this.clear();
    }
  }
  selectPreviousItem() {
    if (this.listMachine.selectPreviousItem()) {
      this.onPopupSelectItem(this.listMachine.selectedItem);
    }
  }
  clear() {
    this.listMachine.clear();
  }

  /**
   * Refreshes the items shown by the dropdown.
   */
  refreshItems() {
    const itemsFunction = this.itemsFunction;
    if (itemsFunction) {
      this.items = [];
      if (this.refreshItemsTimeout !== undefined) {
        clearTimeout(this.refreshItemsTimeout);
        this.refreshItemsTimeout = undefined;
      }
      if (this.currentItemsPromise) {
        this.currentItemsPromise.cancel();
        this.currentItemsPromise = undefined;
      }
      this.refreshItemsTimeout = setTimeout((() => {
        if (!this.currentItemsPromise) {
          this.currentItemsPromise = new CancelablePromise(itemsFunction());
          this.currentItemsPromise.promise.then(items => this.items = items).catch(error => {
            if (!ClassUtils.isInstanceOf(error, PromiseCanceled)) {
              AbDropdownMachine.Logger.error(`Failed to fetch items for dropdown: ${error}`);
            }
          });
        }
      }).bind(this), this.debounceDelay);
    }
  }
  observeItems(onItemsChange) {
    this.listMachine.observeItems(onItemsChange);
  }
}, _defineProperty(_AbDropdownMachine, "Logger", LogUtils.getLogger(__filename)), _AbDropdownMachine), _descriptor = _applyDecoratedDescriptor(_class.prototype, "showingPopup", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_popupPosition", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "onShowPopup", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onShowPopup"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onHidePopup", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onHidePopup"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "popupPosition", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "popupPosition"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "items", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "items"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectedItem", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "selectedItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onPopupSelectItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onPopupSelectItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onItemChosen", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onItemChosen"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onValuesChosenIfChanged", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onValuesChosenIfChanged"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onValuesChosen", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onValuesChosen"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onRemoveValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onRemoveValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectNextItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectNextItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectPreviousItemOrClear", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectPreviousItemOrClear"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectPreviousItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectPreviousItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clear", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clear"), _class.prototype), _class);
//# sourceMappingURL=AbDropdownMachine.js.map
